import { useCallback, useMemo, useState } from 'react'
import { useParams, Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Badge } from './ui/badge'
import { Label } from './ui/label'
import { Checkbox } from './ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { mdiLoading, mdiPlus } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { $api } from '../api/queryClient'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from './ApiSearchableSelect'
import { usePropertyDefinitionSearch } from '../hooks/usePropertyDefinitionSearch'
import { Can } from '../contexts/PermissionsContext'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { EmptyState } from './ui/empty-state'

function invalidateNormalizations(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey as any[]
      if (queryKey.length >= 2 && queryKey[0] === 'get') {
        const path = queryKey[1] as string
        return path.includes('/normalizations')
      }
      return false
    }
  })
}

export function NormalizationsPage() {
  const { org } = useParams({ strict: false })
  const queryClient = useQueryClient()
  const { t } = useTranslation('derivations')
  const [searchQuery, setSearchQuery] = useState('')
  const [enabledFilter, setEnabledFilter] = useState<string>('all')
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [selectedPropertyUid, setSelectedPropertyUid] = useState('')
  const [newNormalization, setNewNormalization] = useState({
    property_code: '',
    name: '',
    enabled: true,
  })

  const { searchPropertyDefinitions, initialOptions, propertyDefinitionsAll } = usePropertyDefinitionSearch(org!)

  const handlePropertySelect = useCallback((option: ApiSearchableSelectOption) => {
    const code = (option.data?.code as string) || propertyDefinitionsAll.find(pd => pd.uid === option.value)?.code || ''
    setNewNormalization(prev => ({ ...prev, property_code: code }))
  }, [propertyDefinitionsAll])

  useBreadcrumbs(useMemo(() => [
    { title: t('common:nav.home'), path: `/${org}` },
    { title: t('common:nav.settings'), path: `/${org}/settings` },
    { title: t('normalizations.breadcrumb') }
  ], [org, t]))

  const queryParams: Record<string, any> = {}
  if (searchQuery) queryParams.q = searchQuery
  if (enabledFilter !== 'all') queryParams.enabled = enabledFilter === 'true'

  const { data, isLoading, error } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/normalizations' as any, {
    params: {
      path: { orgCode: org! },
      query: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    }
  })

  const createMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/normalizations' as any, {
    onSuccess: () => {
      invalidateNormalizations(queryClient)
      setIsCreateDialogOpen(false)
      setSelectedPropertyUid('')
      setNewNormalization({ property_code: '', name: '', enabled: true })
    }
  })

  const normalizations = (data as any)?.data || []
  const total = (data as any)?.total || 0

  const handleCreate = () => {
    createMutation.mutate({
      params: { path: { orgCode: org! } },
      body: {
        property_code: newNormalization.property_code,
        name: newNormalization.name || undefined,
        enabled: newNormalization.enabled,
      },
    } as any)
  }

  return (
    <div className="container mx-auto py-8 max-w-6xl">
      <div className="bg-card rounded-lg shadow-lg">
        <div className="px-6 py-4 border-b border-border">
          <PageHeader
            icon={SECTION_ICONS.normalizations}
            title={t('normalizations.page_title')}
            description={t('normalizations.page_description', { count: total })}
            actions={
              <>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <Can perm="configuration.write">
              <DialogTrigger asChild>
                <Button data-testid="create-normalization-btn">
                  <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                  {t('normalizations.create_button')}
                </Button>
              </DialogTrigger>
            </Can>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('normalizations.create_dialog.title')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 p-4">
                <div>
                  <Label>{t('normalizations.create_dialog.property_label')}</Label>
                  <ApiSearchableSelect
                    value={selectedPropertyUid}
                    onValueChange={(uid) => {
                      setSelectedPropertyUid(uid)
                      if (!uid) {
                        setNewNormalization(prev => ({ ...prev, property_code: '' }))
                      }
                    }}
                    onOptionSelect={handlePropertySelect}
                    onSearch={searchPropertyDefinitions}
                    placeholder={t('normalizations.create_dialog.property_placeholder')}
                    searchPlaceholder={t('normalizations.create_dialog.property_search_placeholder')}
                    emptyMessage={t('normalizations.create_dialog.property_empty')}
                    initialOptions={initialOptions}
                    data-testid="create-normalization-property-code"
                  />
                </div>
                <div>
                  <Label htmlFor="normalization-name">{t('normalizations.create_dialog.name_label')}</Label>
                  <Input
                    id="normalization-name"
                    placeholder={t('normalizations.create_dialog.name_placeholder')}
                    value={newNormalization.name}
                    onChange={(e) => setNewNormalization(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="create-normalization-name"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={newNormalization.enabled}
                    onCheckedChange={(checked) => setNewNormalization(prev => ({ ...prev, enabled: checked === true }))}
                    data-testid="create-normalization-enabled"
                  />
                  <Label>{t('normalizations.create_dialog.enabled_label')}</Label>
                </div>
                {createMutation.isError && (
                  <div className="text-sm text-destructive">
                    {(createMutation.error as any)?.detail || t('normalizations.create_dialog.failed')}
                  </div>
                )}
                <Button
                  onClick={handleCreate}
                  disabled={createMutation.isPending || !newNormalization.property_code}
                  data-testid="create-normalization-submit"
                  className="w-full"
                >
                  {createMutation.isPending ? <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" /> : null}
                  {t('normalizations.create_dialog.submit')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
              </>
            }
          />
        </div>

        <div className="p-6">
          <div className="flex gap-4 mb-4">
            <Input
              placeholder={t('normalizations.search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
              data-testid="normalizations-search"
            />
            <Select value={enabledFilter} onValueChange={setEnabledFilter}>
              <SelectTrigger className="w-[180px]" data-testid="normalizations-enabled-filter">
                <SelectValue placeholder={t('normalizations.filter_status_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common:status.all')}</SelectItem>
                <SelectItem value="true">{t('common:status.enabled')}</SelectItem>
                <SelectItem value="false">{t('common:status.disabled')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <AppIcon path={mdiLoading} size="xl" className="motion-safe:animate-spin" />
            </div>
          ) : error ? (
            <div className="text-destructive text-center py-8">{t('normalizations.failed_to_load')}</div>
          ) : normalizations.length === 0 ? (
            <EmptyState variant="inline" live title={t('normalizations.no_normalizations')} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('normalizations.table.property')}</TableHead>
                  <TableHead>{t('normalizations.table.pipeline')}</TableHead>
                  <TableHead>{t('normalizations.table.operations')}</TableHead>
                  <TableHead>{t('normalizations.table.enabled')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {normalizations.map((n: any) => (
                  <TableRow key={n.uid}>
                    <TableCell>
                      <Link
                        to="/$org/normalizations/$uid"
                        params={{ org: org!, uid: n.uid }}
                        className="text-primary hover:underline font-medium"
                        data-testid={`normalization-link-${n.property?.code || n.uid}`}
                      >
                        {n.name || n.property?.name?.en || n.property?.code || n.uid}
                      </Link>
                      {n.property?.code && (
                        <div className="text-xs text-muted-foreground font-mono">{n.property.code}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <code className="text-sm text-muted-foreground">{n.pipeline_summary}</code>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{t('normalizations.badges.ops_count', { count: n.operations_count })}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={n.enabled ? 'default' : 'secondary'}>
                        {n.enabled ? t('common:status.enabled') : t('common:status.disabled')}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  )
}
